// src/analytics/UIDesignSystemEngine.js
// Professional UI/UX Design System Engine for MLB-grade presentation

export class UIDesignSystemEngine {
  constructor() {
    this.designTokens = this.initializeDesignTokens();
    this.componentStyles = this.initializeComponentStyles();
    this.layoutSystems = this.initializeLayoutSystems();
    this.animationLibrary = this.initializeAnimations();
    this.responsiveBreakpoints = this.initializeBreakpoints();
  }

  initializeDesignTokens() {
    return {
      // Thunder Brand Colors
      colors: {
        primary: {
          thunder: '#FFB800',      // Thunder Gold
          thunderDark: '#E6A600',  // Darker Gold
          thunderLight: '#FFD700', // Light Gold
          accent: '#1E3A8A',       // Thunder Blue
          accentLight: '#3B82F6'   // Light Blue
        },
        neutral: {
          slate900: '#0F172A',     // Deep background
          slate800: '#1E293B',     // Card backgrounds
          slate700: '#334155',     // Borders
          slate600: '#475569',     // Muted text
          slate400: '#94A3B8',     // Secondary text
          slate200: '#E2E8F0',     // Light text
          white: '#FFFFFF'         // Primary text
        },
        semantic: {
          success: '#10B981',      // Green for positive metrics
          warning: '#F59E0B',      // Amber for caution
          error: '#EF4444',       // Red for negative metrics
          info: '#3B82F6',        // Blue for information
          purple: '#8B5CF6',      // Purple for special metrics
          emerald: '#059669',     // Emerald for excellence
          rose: '#F43F5E'         // Rose for alerts
        },
        gradients: {
          primary: 'linear-gradient(135deg, #FFB800 0%, #E6A600 100%)',
          background: 'linear-gradient(180deg, #0F172A 0%, #1E293B 100%)',
          card: 'linear-gradient(145deg, #1E293B 0%, #334155 100%)',
          accent: 'linear-gradient(135deg, #1E3A8A 0%, #3B82F6 100%)'
        }
      },

      // Typography Scale
      typography: {
        fontFamily: {
          primary: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          mono: '"JetBrains Mono", "Fira Code", Consolas, monospace',
          display: '"Poppins", "Inter", sans-serif'
        },
        fontSize: {
          xs: '0.75rem',    // 12px
          sm: '0.875rem',   // 14px
          base: '1rem',     // 16px
          lg: '1.125rem',   // 18px
          xl: '1.25rem',    // 20px
          '2xl': '1.5rem',  // 24px
          '3xl': '1.875rem', // 30px
          '4xl': '2.25rem', // 36px
          '5xl': '3rem',    // 48px
          '6xl': '3.75rem'  // 60px
        },
        fontWeight: {
          light: 300,
          normal: 400,
          medium: 500,
          semibold: 600,
          bold: 700,
          extrabold: 800,
          black: 900
        },
        lineHeight: {
          tight: 1.25,
          snug: 1.375,
          normal: 1.5,
          relaxed: 1.625,
          loose: 2
        },
        letterSpacing: {
          tighter: '-0.05em',
          tight: '-0.025em',
          normal: '0em',
          wide: '0.025em',
          wider: '0.05em',
          widest: '0.1em'
        }
      },

      // Spacing Scale
      spacing: {
        px: '1px',
        0: '0',
        1: '0.25rem',   // 4px
        2: '0.5rem',    // 8px
        3: '0.75rem',   // 12px
        4: '1rem',      // 16px
        5: '1.25rem',   // 20px
        6: '1.5rem',    // 24px
        8: '2rem',      // 32px
        10: '2.5rem',   // 40px
        12: '3rem',     // 48px
        16: '4rem',     // 64px
        20: '5rem',     // 80px
        24: '6rem',     // 96px
        32: '8rem',     // 128px
        40: '10rem',    // 160px
        48: '12rem',    // 192px
        56: '14rem',    // 224px
        64: '16rem'     // 256px
      },

      // Border Radius
      borderRadius: {
        none: '0',
        sm: '0.125rem',   // 2px
        base: '0.25rem',  // 4px
        md: '0.375rem',   // 6px
        lg: '0.5rem',     // 8px
        xl: '0.75rem',    // 12px
        '2xl': '1rem',    // 16px
        '3xl': '1.5rem',  // 24px
        full: '9999px'
      },

      // Shadows
      boxShadow: {
        sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        thunder: '0 20px 25px -5px rgba(255, 184, 0, 0.1), 0 10px 10px -5px rgba(255, 184, 0, 0.04)',
        glow: '0 0 20px rgba(255, 184, 0, 0.3)'
      }
    };
  }

  initializeComponentStyles() {
    return {
      // Page Layout Components
      pageContainer: {
        base: {
          minHeight: '100vh',
          background: this.designTokens.colors.gradients.background,
          fontFamily: this.designTokens.typography.fontFamily.primary,
          color: this.designTokens.colors.neutral.white
        },
        padding: {
          mobile: this.designTokens.spacing[4],
          tablet: this.designTokens.spacing[6],
          desktop: this.designTokens.spacing[8]
        }
      },

      // Header Components
      pageHeader: {
        container: {
          marginBottom: this.designTokens.spacing[8],
          textAlign: 'center'
        },
        title: {
          fontSize: this.designTokens.typography.fontSize['4xl'],
          fontWeight: this.designTokens.typography.fontWeight.black,
          color: this.designTokens.colors.primary.thunder,
          marginBottom: this.designTokens.spacing[2],
          letterSpacing: this.designTokens.typography.letterSpacing.tight,
          textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
        },
        subtitle: {
          fontSize: this.designTokens.typography.fontSize.lg,
          color: this.designTokens.colors.neutral.slate400,
          fontWeight: this.designTokens.typography.fontWeight.medium,
          lineHeight: this.designTokens.typography.lineHeight.relaxed
        }
      },

      // Card Components
      card: {
        base: {
          background: this.designTokens.colors.gradients.card,
          borderRadius: this.designTokens.borderRadius.xl,
          padding: this.designTokens.spacing[6],
          border: `1px solid ${this.designTokens.colors.neutral.slate700}`,
          boxShadow: this.designTokens.boxShadow.lg,
          transition: 'all 0.2s ease-in-out'
        },
        hover: {
          transform: 'translateY(-2px)',
          boxShadow: this.designTokens.boxShadow.thunder,
          borderColor: this.designTokens.colors.primary.thunder
        },
        header: {
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: this.designTokens.spacing[4]
        },
        title: {
          fontSize: this.designTokens.typography.fontSize.lg,
          fontWeight: this.designTokens.typography.fontWeight.bold,
          color: this.designTokens.colors.primary.thunder
        },
        content: {
          color: this.designTokens.colors.neutral.slate200
        }
      },

      // Metric Card Components
      metricCard: {
        container: {
          background: this.designTokens.colors.neutral.slate800,
          borderRadius: this.designTokens.borderRadius.lg,
          padding: this.designTokens.spacing[4],
          border: `1px solid ${this.designTokens.colors.neutral.slate700}`,
          transition: 'all 0.2s ease-in-out'
        },
        label: {
          fontSize: this.designTokens.typography.fontSize.sm,
          fontWeight: this.designTokens.typography.fontWeight.semibold,
          color: this.designTokens.colors.neutral.slate400,
          marginBottom: this.designTokens.spacing[1]
        },
        value: {
          fontSize: this.designTokens.typography.fontSize['2xl'],
          fontWeight: this.designTokens.typography.fontWeight.bold,
          color: this.designTokens.colors.neutral.white
        },
        change: {
          fontSize: this.designTokens.typography.fontSize.sm,
          fontWeight: this.designTokens.typography.fontWeight.medium,
          marginTop: this.designTokens.spacing[1]
        }
      },

      // Button Components
      button: {
        base: {
          fontFamily: this.designTokens.typography.fontFamily.primary,
          fontWeight: this.designTokens.typography.fontWeight.semibold,
          borderRadius: this.designTokens.borderRadius.lg,
          padding: `${this.designTokens.spacing[3]} ${this.designTokens.spacing[6]}`,
          transition: 'all 0.2s ease-in-out',
          cursor: 'pointer',
          border: 'none',
          outline: 'none'
        },
        primary: {
          background: this.designTokens.colors.gradients.primary,
          color: this.designTokens.colors.neutral.slate900,
          boxShadow: this.designTokens.boxShadow.md
        },
        secondary: {
          background: this.designTokens.colors.neutral.slate700,
          color: this.designTokens.colors.neutral.white,
          border: `1px solid ${this.designTokens.colors.neutral.slate600}`
        },
        ghost: {
          background: 'transparent',
          color: this.designTokens.colors.neutral.slate400,
          border: `1px solid ${this.designTokens.colors.neutral.slate700}`
        }
      },

      // Tab Components
      tabs: {
        container: {
          background: this.designTokens.colors.neutral.slate800,
          borderRadius: this.designTokens.borderRadius.lg,
          padding: this.designTokens.spacing[1],
          display: 'flex',
          gap: this.designTokens.spacing[1]
        },
        tab: {
          padding: `${this.designTokens.spacing[3]} ${this.designTokens.spacing[6]}`,
          borderRadius: this.designTokens.borderRadius.md,
          fontWeight: this.designTokens.typography.fontWeight.semibold,
          transition: 'all 0.2s ease-in-out',
          cursor: 'pointer'
        },
        active: {
          background: this.designTokens.colors.primary.thunder,
          color: this.designTokens.colors.neutral.slate900
        },
        inactive: {
          color: this.designTokens.colors.neutral.slate400,
          ':hover': {
            color: this.designTokens.colors.neutral.white,
            background: this.designTokens.colors.neutral.slate700
          }
        }
      },

      // Form Components
      form: {
        select: {
          background: this.designTokens.colors.neutral.slate800,
          color: this.designTokens.colors.neutral.white,
          border: `1px solid ${this.designTokens.colors.primary.thunder}`,
          borderRadius: this.designTokens.borderRadius.lg,
          padding: `${this.designTokens.spacing[2]} ${this.designTokens.spacing[4]}`,
          fontSize: this.designTokens.typography.fontSize.base,
          fontWeight: this.designTokens.typography.fontWeight.semibold,
          outline: 'none',
          transition: 'all 0.2s ease-in-out'
        },
        input: {
          background: this.designTokens.colors.neutral.slate800,
          color: this.designTokens.colors.neutral.white,
          border: `1px solid ${this.designTokens.colors.neutral.slate700}`,
          borderRadius: this.designTokens.borderRadius.md,
          padding: `${this.designTokens.spacing[3]} ${this.designTokens.spacing[4]}`,
          fontSize: this.designTokens.typography.fontSize.base,
          outline: 'none',
          transition: 'all 0.2s ease-in-out'
        }
      },

      // Badge Components
      badge: {
        base: {
          display: 'inline-block',
          padding: `${this.designTokens.spacing[1]} ${this.designTokens.spacing[3]}`,
          borderRadius: this.designTokens.borderRadius.full,
          fontSize: this.designTokens.typography.fontSize.xs,
          fontWeight: this.designTokens.typography.fontWeight.bold,
          textTransform: 'uppercase',
          letterSpacing: this.designTokens.typography.letterSpacing.wide
        },
        primary: {
          background: this.designTokens.colors.primary.thunder,
          color: this.designTokens.colors.neutral.slate900
        },
        success: {
          background: this.designTokens.colors.semantic.success,
          color: this.designTokens.colors.neutral.white
        },
        warning: {
          background: this.designTokens.colors.semantic.warning,
          color: this.designTokens.colors.neutral.slate900
        },
        error: {
          background: this.designTokens.colors.semantic.error,
          color: this.designTokens.colors.neutral.white
        }
      }
    };
  }

  initializeLayoutSystems() {
    return {
      // Grid Systems
      grid: {
        container: {
          display: 'grid',
          gap: this.designTokens.spacing[6],
          width: '100%',
          maxWidth: '1280px',
          margin: '0 auto',
          padding: `0 ${this.designTokens.spacing[4]}`
        },
        cols1: { gridTemplateColumns: '1fr' },
        cols2: { gridTemplateColumns: 'repeat(2, 1fr)' },
        cols3: { gridTemplateColumns: 'repeat(3, 1fr)' },
        cols4: { gridTemplateColumns: 'repeat(4, 1fr)' },
        colsAuto: { gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }
      },

      // Flex Systems
      flex: {
        center: {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        },
        between: {
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        },
        start: {
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center'
        },
        column: {
          display: 'flex',
          flexDirection: 'column'
        }
      },

      // Responsive Containers
      responsive: {
        mobile: { maxWidth: '640px' },
        tablet: { maxWidth: '768px' },
        laptop: { maxWidth: '1024px' },
        desktop: { maxWidth: '1280px' },
        wide: { maxWidth: '1536px' }
      }
    };
  }

  initializeBreakpoints() {
    return {
      sm: '640px',
      md: '768px',
      lg: '1024px',
      xl: '1280px',
      '2xl': '1536px'
    };
  }

  initializeAnimations() {
    return {
      // Transition Presets
      transitions: {
        fast: 'all 0.15s ease-in-out',
        base: 'all 0.2s ease-in-out',
        slow: 'all 0.3s ease-in-out'
      },

      // Animation Keyframes
      keyframes: {
        fadeIn: {
          from: { opacity: 0 },
          to: { opacity: 1 }
        },
        slideUp: {
          from: { transform: 'translateY(20px)', opacity: 0 },
          to: { transform: 'translateY(0)', opacity: 1 }
        },
        slideDown: {
          from: { transform: 'translateY(-20px)', opacity: 0 },
          to: { transform: 'translateY(0)', opacity: 1 }
        },
        scaleIn: {
          from: { transform: 'scale(0.95)', opacity: 0 },
          to: { transform: 'scale(1)', opacity: 1 }
        },
        pulse: {
          '0%, 100%': { opacity: 1 },
          '50%': { opacity: 0.5 }
        },
        bounce: {
          '0%, 20%, 53%, 80%, 100%': { transform: 'translate3d(0,0,0)' },
          '40%, 43%': { transform: 'translate3d(0,-30px,0)' },
          '70%': { transform: 'translate3d(0,-15px,0)' },
          '90%': { transform: 'translate3d(0,-4px,0)' }
        }
      }
    };
  }

  // Generate CSS-in-JS styles for components
  generateComponentStyles(componentType, variant = 'base', state = 'default') {
    const component = this.componentStyles[componentType];
    if (!component) return {};

    let styles = { ...component.base };
    
    if (variant !== 'base' && component[variant]) {
      styles = { ...styles, ...component[variant] };
    }

    if (state !== 'default' && component[state]) {
      styles = { ...styles, ...component[state] };
    }

    return styles;
  }

  // Generate Tailwind CSS classes
  generateTailwindClasses(componentType, variant = 'base') {
    const classMap = {
      pageContainer: 'min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 font-sans text-white',
      pageHeader: 'mb-8 text-center',
      pageTitle: 'text-4xl font-black text-yellow-400 mb-2 tracking-tight drop-shadow-lg',
      pageSubtitle: 'text-lg text-slate-400 font-medium leading-relaxed',
      card: 'bg-gradient-to-br from-slate-800 to-slate-700 rounded-xl p-6 border border-slate-700 shadow-lg transition-all duration-200 hover:shadow-yellow-400/10 hover:-translate-y-0.5 hover:border-yellow-400',
      metricCard: 'bg-slate-800 rounded-lg p-4 border border-slate-700 transition-all duration-200',
      button: {
        primary: 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-slate-900 font-semibold rounded-lg px-6 py-3 shadow-md transition-all duration-200 hover:shadow-lg hover:scale-105',
        secondary: 'bg-slate-700 text-white font-semibold rounded-lg px-6 py-3 border border-slate-600 transition-all duration-200 hover:bg-slate-600',
        ghost: 'bg-transparent text-slate-400 font-semibold rounded-lg px-6 py-3 border border-slate-700 transition-all duration-200 hover:text-white hover:bg-slate-700'
      },
      tabs: 'bg-slate-800 rounded-lg p-1 flex gap-1',
      tabActive: 'bg-yellow-400 text-slate-900 font-semibold px-6 py-3 rounded-md transition-all duration-200',
      tabInactive: 'text-slate-400 font-semibold px-6 py-3 rounded-md transition-all duration-200 hover:text-white hover:bg-slate-700',
      badge: {
        primary: 'inline-block px-3 py-1 bg-yellow-400 text-slate-900 text-xs font-bold uppercase tracking-wide rounded-full',
        success: 'inline-block px-3 py-1 bg-green-500 text-white text-xs font-bold uppercase tracking-wide rounded-full',
        warning: 'inline-block px-3 py-1 bg-yellow-500 text-slate-900 text-xs font-bold uppercase tracking-wide rounded-full',
        error: 'inline-block px-3 py-1 bg-red-500 text-white text-xs font-bold uppercase tracking-wide rounded-full'
      }
    };

    const classes = classMap[componentType];
    if (typeof classes === 'object' && variant) {
      return classes[variant] || classes.base || '';
    }
    return classes || '';
  }

  // Generate complete theme object for styled-components or emotion
  generateTheme() {
    return {
      colors: this.designTokens.colors,
      typography: this.designTokens.typography,
      spacing: this.designTokens.spacing,
      borderRadius: this.designTokens.borderRadius,
      boxShadow: this.designTokens.boxShadow,
      breakpoints: this.responsiveBreakpoints,
      animations: this.animationLibrary
    };
  }

  // Generate CSS custom properties for global use
  generateCSSCustomProperties() {
    const cssVars = {};
    
    // Colors
    Object.entries(this.designTokens.colors.primary).forEach(([key, value]) => {
      cssVars[`--color-primary-${key}`] = value;
    });
    
    Object.entries(this.designTokens.colors.neutral).forEach(([key, value]) => {
      cssVars[`--color-neutral-${key}`] = value;
    });
    
    Object.entries(this.designTokens.colors.semantic).forEach(([key, value]) => {
      cssVars[`--color-semantic-${key}`] = value;
    });

    // Typography
    Object.entries(this.designTokens.typography.fontSize).forEach(([key, value]) => {
      cssVars[`--font-size-${key}`] = value;
    });

    // Spacing
    Object.entries(this.designTokens.spacing).forEach(([key, value]) => {
      cssVars[`--spacing-${key}`] = value;
    });

    return cssVars;
  }

  // Apply professional styling to existing components
  applyProfessionalStyling(element, componentType, variant = 'base') {
    if (!element) return;

    const styles = this.generateComponentStyles(componentType, variant);
    Object.assign(element.style, styles);
  }

  // Generate responsive grid classes
  generateResponsiveGrid(columns = { mobile: 1, tablet: 2, desktop: 3, wide: 4 }) {
    return {
      display: 'grid',
      gap: this.designTokens.spacing[6],
      gridTemplateColumns: `repeat(${columns.mobile}, 1fr)`,
      [`@media (min-width: ${this.responsiveBreakpoints.md})`]: {
        gridTemplateColumns: `repeat(${columns.tablet}, 1fr)`
      },
      [`@media (min-width: ${this.responsiveBreakpoints.lg})`]: {
        gridTemplateColumns: `repeat(${columns.desktop}, 1fr)`
      },
      [`@media (min-width: ${this.responsiveBreakpoints.xl})`]: {
        gridTemplateColumns: `repeat(${columns.wide}, 1fr)`
      }
    };
  }

  // Performance color coding for metrics
  getPerformanceColor(value, benchmark, isInverse = false) {
    const ratio = value / benchmark;
    
    if (isInverse) {
      if (ratio <= 0.85) return this.designTokens.colors.semantic.success;
      if (ratio <= 1.15) return this.designTokens.colors.semantic.warning;
      return this.designTokens.colors.semantic.error;
    } else {
      if (ratio >= 1.15) return this.designTokens.colors.semantic.success;
      if (ratio >= 0.85) return this.designTokens.colors.semantic.warning;
      return this.designTokens.colors.semantic.error;
    }
  }

  // Generate professional chart color palette
  getChartColorPalette() {
    return [
      this.designTokens.colors.primary.thunder,
      this.designTokens.colors.semantic.success,
      this.designTokens.colors.semantic.info,
      this.designTokens.colors.semantic.purple,
      this.designTokens.colors.semantic.warning,
      this.designTokens.colors.semantic.emerald,
      this.designTokens.colors.semantic.rose,
      this.designTokens.colors.primary.accent
    ];
  }
}

export default UIDesignSystemEngine;
